import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { faCalendarWeek } from '@fortawesome/pro-regular-svg-icons/faCalendarWeek';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Col, Flex, Form, Row, Space, theme } from 'antd';
import dayjs from 'dayjs';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { PosDatePicker } from 'pos-client/components/shared/Form/PosDatePicker.tsx';
import { PosSelect } from 'pos-client/components/shared/Form/PosSelect.tsx';
import PosText from 'pos-client/components/shared/General/PosText.tsx';
import { useConfirmModal } from 'pos-client/hooks/useConfirmModal.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { fullWidth } from 'pos-client/utils/cssHelpers.ts';
import { dateFormatString } from 'pos-client/utils/dateHelpers.ts';
import { isDayBeforeToday } from 'pos-client/utils/dayjs.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  type ScheduleLaneFormModel,
  ScheduleLaneFormSchema,
  type ScheduleLaneInputModel,
  type ScheduleLaneModel,
} from 'pos-shared/models/openingHoursGroupSchema.ts';

import { useWeekdays } from './useWeekdays.tsx';

interface ModalProps {
  data?: ScheduleLaneModel;
  tableData?: ScheduleLaneModel[];
  handleOk: () => void;
  handleCancel: () => void;
}

export const CreateEditScheduleLanesModal = ({
  data,
  tableData,
  handleOk,
  handleCancel,
}: ModalProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { id } = useParams();

  const editMode = !!data;
  const weekdays = useWeekdays();

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = useForm<ScheduleLaneFormModel>({
    mode: 'all',
    defaultValues: {
      ...(data && {
        ...data,
        dateFrom: dayjs(data.dateFrom),
        dateTo: dayjs(data.dateTo),
      }),
    },
    resolver: zodResolver(ScheduleLaneFormSchema),
  });

  const { showSuccess, showError } = useNotifications();

  const { getAllScheduleLanes } = trpc.useContext();
  const [enableSubmit, setEnableSubmit] = useState(false);
  const modalTitle = editMode
    ? t('scheduling.updateDefaultSchedule')
    : t('scheduling.createDefaultSchedule');

  const { showModal: showUnsavedChangesModal } = useConfirmModal({
    type: 'delete',
    title: t('common.confirmCancel.unsavedChanges'),
    content: t('common.confirmCancel.unsavedChangesConfirm'),
    confirmText: t('common.buttons.discardChanges'),
    confirmProps: {
      ghost: true,
    },
    onConfirm: handleCancel,
  });

  const onCancelModal = () => {
    if (isDirty) {
      showUnsavedChangesModal();
    } else {
      handleCancel();
    }
  };

  const onSuccessMutation = async () => {
    await getAllScheduleLanes.invalidate();
    await handleOk();
  };

  const { mutate: createScheduleLane, isLoading: isCreating } =
    trpc.createScheduleLane.useMutation({
      onSuccess: async () => {
        onSuccessMutation();
        showSuccess(
          t('common.notification.success.message', {
            action: 'added',
            entity: 'schedule lane',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'add',
            entity: 'schedule lane',
          })
        );
      },
    });
  const { mutate: updateScheduleLane, isLoading: isUpdating } =
    trpc.updateScheduleLane.useMutation({
      onSuccess: async () => {
        onSuccessMutation();
        showSuccess(
          t('common.notification.success.message', {
            action: 'updated',
            entity: 'schedule lane',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'update',
            entity: 'schedule lane',
          })
        );
      },
    });
  const { mutateAsync: deleteScheduleLane, isLoading: isDeleting } =
    trpc.deleteScheduleLane.useMutation({
      onSuccess: async () => {
        onSuccessMutation();
        showSuccess(
          t('common.notification.success.message', {
            action: 'deleted',
            entity: 'schedule lane',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'delete',
            entity: 'schedule lane',
          })
        );
      },
    });

  const saveScheduleLane = handleSubmit((formData) => {
    const formattedData = {
      ...formData,
      dateFrom: formData.dateFrom.toISOString(),
      dateTo: formData.dateTo.toISOString(),
    };
    if (editMode) {
      const updateData: ScheduleLaneInputModel = {
        openingHoursId: id || '',
        scheduleLaneId: data.id,
        data: {
          id: data?.id,
          ...formattedData,
        },
      };
      updateScheduleLane(updateData);
    } else {
      createScheduleLane({ openingHoursId: id || '', data: formattedData });
    }
  });

  const { showModal: confirmRemoveScheduleLane } = useConfirmModal({
    type: 'delete',
    title: t('scheduling.removeDefaultSchedule'),
    content: t('scheduling.removeDefaultScheduleConfirm'),
    confirmText: t('common.buttons.delete'),
    confirmProps: {
      ghost: true,
    },
    isLoading: isDeleting,
  });

  const handleRemoveScheduleLane = () =>
    confirmRemoveScheduleLane({
      async onConfirm() {
        await deleteScheduleLane({
          openingHoursId: id || '',
          scheduleLaneId: data?.id || '',
        });
      },
    });

  const day = watch()?.dayOfTheWeek;

  const hasDayOfTheWeek = tableData?.some((lane) =>
    day?.includes(lane?.dayOfTheWeek)
  );
  const hasDateRange = watch()?.dateFrom && watch()?.dateTo;
  useEffect(() => {
    if (typeof watch().dayOfTheWeek !== 'undefined') {
      setEnableSubmit(!hasDayOfTheWeek && !!hasDateRange);
    }
  }, [watch, hasDayOfTheWeek, hasDateRange]);

  return (
    <PosModal
      header={{ title: modalTitle, icon: faCalendarWeek }}
      open
      onCancel={onCancelModal}
      footer={
        <Flex justify="space-between" style={fullWidth}>
          <Button
            danger
            ghost
            onClick={handleRemoveScheduleLane}
            hidden={!editMode}
          >
            {t('common.buttons.delete')}
          </Button>
          <Space style={{ flexGrow: 1, justifyContent: 'flex-end' }}>
            <Button type="default" onClick={onCancelModal}>
              {t('common.buttons.cancel')}
            </Button>
            <Button
              type="primary"
              onClick={saveScheduleLane}
              loading={isCreating || isUpdating}
              disabled={!isDirty || !enableSubmit}
            >
              {t(editMode ? 'common.buttons.update' : 'common.buttons.create')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form layout="vertical">
        {hasDayOfTheWeek && (
          <PosText icon={faExclamationTriangle} color={token.colorWarningText}>
            {t('scheduling.existingDefaultScheduleDay')}
          </PosText>
        )}
        <PosSelect
          control={control}
          name="dayOfTheWeek"
          label={t('common.time.unit.day')}
          options={weekdays}
          showSearch
          required
          mode={!editMode ? 'multiple' : undefined}
        />

        <Row gutter={[token.paddingSM, token.paddingSM]}>
          <Col span={12}>
            <PosDatePicker
              control={control}
              name="dateFrom"
              label={t('common.date.from')}
              style={{ width: '100%' }}
              disabledDate={isDayBeforeToday}
              format={dateFormatString}
              required
            />
          </Col>
          <Col span={12}>
            <PosDatePicker
              control={control}
              name="dateTo"
              label={t('common.date.to')}
              style={{ width: '100%' }}
              disabledDate={(current) =>
                current && current < dayjs(watch()?.dateFrom).endOf('day')
              }
              format={dateFormatString}
              required
            />
          </Col>
        </Row>
      </Form>
    </PosModal>
  );
};
